<template lang="pug">
v-tooltip(top v-if="disabled" )
  template(v-slot:activator="{ attrs, on }")
    div(v-bind="attrs" v-on="on" :class="{'show-dropzone': showDropzoneIndication && isDragging}")
      editor-content(:editor="editor")
  | Nur Gruppenmitglieder können Beiträge schreiben, da die Veranstaltung intern ist.
div(v-else :class="{'show-dropzone': showDropzoneIndication && isDragging}")
  editor-content(:editor="editor")
</template>

<script lang="ts">
import Vue, { computed, onMounted } from "vue"
import { EditorContent, Extensions } from "@tiptap/vue-2"
import { Editor } from "@tiptap/core"
import { ref, Ref, watch } from "vue"
import { useCoreWysiwyg } from "components/Wysiwyg/useWysiwygEditor"
import { useDragDetection } from "components/Wysiwyg/use-drag-detection"

export default Vue.extend({
  components: {
    EditorContent,
  },
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    inline: { type: Boolean, default: false }, // note that this prop is not reactive, because it is set during mount
    extensions: { type: Array as () => Extensions, default: () => [] },
    showDropzoneIndication: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  beforeDestroy() {
    this.editor.value?.destroy()
  },
  setup(props, { emit }) {
    const editor = ref<Editor | null>(null)

    const cssClasses = computed(
      () =>
        "suus__editor__wysiwyg" + (props.inline ? " suus__editor--inline" : "")
    )

    onMounted(() => {
      const editorContent: Ref<string> = ref(props.value)

      editor.value = useCoreWysiwyg(
        editorContent,
        props.placeholder,
        cssClasses.value,
        props.extensions,
        props.disabled
      )

      emit("editorReady", editor.value)

      watch(editorContent, () => emit("input", editorContent.value))
    })

    const { isDragging } = useDragDetection()

    watch(
      () => props.value,
      (newVal: string) => {
        if (newVal === "") {
          editor.value?.commands.clearContent()
          emit("content-cleared", true)
          return
        }
        if (editor.value?.getHTML() !== newVal) {
          editor.value?.commands.setContent(newVal, false)
        }
      }
    )

    watch(
      () => props.disabled,
      (newVal: boolean) => {
        if (newVal) {
          editor.value?.setEditable(false)
        } else {
          editor.value?.setEditable(true)
        }
      },
      { immediate: true }
    )

    return {
      editor,
      isDragging,
    }
  },
})
</script>

<style lang="sass">
$primary-color: rgba(50, 131, 247, 1)
$text-color: rgba(66, 66, 66, 1)
%font-stack
  font-family: system-ui !important
  color: $text-color

.suus__editor__wysiwyg
  overflow-x: auto

.suus__editor
  &__wysiwyg
    min-height: 120px
    background-color: #f5f5f5
    border-top-right-radius: 2px
    border-top-left-radius: 2px
    border-bottom: 1px solid #424242
    padding: 16px
    @extend %font-stack

    &:focus,
    &:active
      transition: border-bottom .4s
      border-bottom: 2px solid $primary-color
      outline: none

    > * + *
      margin-top: 0.75em

    ul,
    ol
      padding: 0 1rem

    h1,
    h2,
    h3,
    h4,
    h5,
    h6
      line-height: 1.1

    pre
      background: #0D0D0D
      color: #FFF
      padding: 0.75rem 1rem
      border-radius: 0.5rem

    blockquote
      padding-left: 1rem
      border-left: 2px solid rgba($primary-color, 0.1)

    a
      color: $primary-color

    mark
      background-color: #ffe066

    p.is-editor-empty:first-child::before
      content: attr(data-placeholder)
      font-size: 0.9rem
      float: left
      color: #939393 !important
      pointer-events: none
      height: 0

  &--inline
    background-color: #fff
    color: $text-color
    border: 1px solid #e5e5e5

  &--dark
    background-color: rgba(#f5f5f5, 0.1)
    color: #fff


.mention
  border: 1px solid rgba(34, 36, 38, 0.15) !important
  border-radius: 0.14285714rem !important
  transition: background 0.1s ease !important
  padding: 0.283rem 0.433rem !important
  font-size: 0.875em !important
  background: #307ef7 !important
  color: white !important
  font-weight: 500 !important
  display: inline-block !important
  white-space: nowrap !important
  line-height: initial !important
  margin: 0 0.25em !important

  &:before
    content: "@"
    font-family: "Icons"
    font-size: 1.2em
    vertical-align: sub
    padding-right: 0.5rem
    font-weight: 600

  &.group-mention
    &:before
      content: "\eef8"

  &.member-mention
    &:before
      content: "\f075"

.suus__editor__wysiwyg__image
  border: 4px solid transparent
  vertical-align: bottom

  &.ProseMirror-selectednode
    border-color: #307ef7

.show-dropzone
  border: 3px dashed #307ef7
</style>
